import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ErrorState } from '@ui-kit';
import type { SystemSettingResponse } from '../types/setting';
import {
  buildSettingValueSchema,
  type SettingEditFormValues,
} from '../schema/settingSchema';

interface SettingEditDialogProps {
  open: boolean;
  setting: SystemSettingResponse | null;
  isLoading: boolean;
  errorMessage?: string;
  errorCode?: string;
  onClose: () => void;
  onSubmit: (key: string, value: string) => void;
}

/* ------------------------------------------------------------------ */
/* Data-type specific widgets                                           */
/* ------------------------------------------------------------------ */

interface BooleanToggleFieldProps {
  value: string;
  onChange: (v: string) => void;
  id: string;
}
function BooleanToggleField({ value, onChange, id }: BooleanToggleFieldProps) {
  const { t } = useTranslation();
  const checked = value === 'true';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
      <button
        id={id}
        role="switch"
        type="button"
        aria-checked={checked}
        aria-label={t('systemSettings.edit.field.value.hint.BOOLEAN')}
        onClick={() => onChange(checked ? 'false' : 'true')}
        style={{
          position: 'relative',
          width: 44,
          height: 24,
          borderRadius: 'var(--radius-full)',
          background: checked ? 'var(--color-brand-primary)' : 'var(--color-surface-border)',
          border: 'none',
          cursor: 'pointer',
          transition: 'background 0.2s',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 3,
            left: checked ? 23 : 3,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: 'var(--color-surface-background)',
            transition: 'left 0.2s',
          }}
        />
      </button>
      <span
        style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}
      >
        {checked ? 'true' : 'false'}
      </span>
    </div>
  );
}

interface IntegerInputFieldProps {
  value: string;
  onChange: (v: string) => void;
  min?: number | null;
  max?: number | null;
  id: string;
  hasError: boolean;
}
function IntegerInputField({ value, onChange, min, max, id, hasError }: IntegerInputFieldProps) {
  return (
    <input
      id={id}
      type="number"
      value={value}
      min={min ?? undefined}
      max={max ?? undefined}
      step={1}
      aria-invalid={hasError}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: 'var(--spacing-2) var(--spacing-3)',
        border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-primary)',
        background: 'var(--color-surface-elevated)',
        fontFamily: 'var(--font-family-mono)',
        width: '100%',
        outline: 'none',
      }}
    />
  );
}

interface StringInputFieldProps {
  value: string;
  onChange: (v: string) => void;
  id: string;
  hasError: boolean;
}
function StringInputField({ value, onChange, id, hasError }: StringInputFieldProps) {
  return (
    <input
      id={id}
      type="text"
      value={value}
      maxLength={512}
      aria-invalid={hasError}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: 'var(--spacing-2) var(--spacing-3)',
        border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-primary)',
        background: 'var(--color-surface-elevated)',
        fontFamily: 'var(--font-family-sans)',
        width: '100%',
        outline: 'none',
      }}
    />
  );
}

interface EnumSelectFieldProps {
  value: string;
  onChange: (v: string) => void;
  enumValues: string;
  id: string;
  hasError: boolean;
}
function EnumSelectField({ value, onChange, enumValues, id, hasError }: EnumSelectFieldProps) {
  const options = enumValues.split(',').map((s) => s.trim()).filter(Boolean);
  return (
    <select
      id={id}
      value={value}
      aria-invalid={hasError}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: 'var(--spacing-2) var(--spacing-3)',
        border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-primary)',
        background: 'var(--color-surface-elevated)',
        fontFamily: 'var(--font-family-sans)',
        width: '100%',
        outline: 'none',
      }}
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

/* ------------------------------------------------------------------ */
/* Main dialog                                                          */
/* ------------------------------------------------------------------ */

export function SettingEditDialog({
  open,
  setting,
  isLoading,
  errorMessage,
  errorCode,
  onClose,
  onSubmit,
}: SettingEditDialogProps) {
  const { t } = useTranslation();
  const schema = setting
    ? buildSettingValueSchema(setting.dataType, {
        minValue: setting.minValue,
        maxValue: setting.maxValue,
        enumValues: setting.enumValues ?? undefined,
      })
    : buildSettingValueSchema('STRING');

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<SettingEditFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { value: setting?.value ?? '' },
  });

  // Reset form whenever the dialog opens with a new setting
  useEffect(() => {
    if (open && setting) {
      reset({ value: setting.value });
    }
  }, [open, setting, reset]);

  // Close on Escape.
  // ★2026-07-15 실결함: 핸들러가 다이얼로그 DOM 에만 붙어 있어, 포커스가 다이얼로그 밖(예: 방금
  // 누른 [수정] 버튼)에 있으면 ESC 가 여기까지 오지 않아 닫히지 않았다(R8-E-004 회귀 테스트가
  // 장기간 red 였던 실제 원인). 열려 있는 동안 문서 레벨에서 듣는다.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !setting) return null;

  const currentValue = watch('value');
  const fieldId = 'setting-edit-value';
  const errorId = 'setting-edit-value-error';

  const handleFormSubmit = (values: SettingEditFormValues) => {
    onSubmit(setting.settingKey, values.value);
  };


  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="setting-edit-title"
      tabIndex={-1}
      autoFocus
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-overlay-backdrop)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        outline: 'none',
      }}
      onClick={onClose}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: '100%',
          maxWidth: 520,
          boxShadow: 'var(--shadow-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-4)',
          margin: 'var(--spacing-4)',
        }}
      >
        {/* Title */}
        <h2
          id="setting-edit-title"
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('systemSettings.edit.title', { settingKey: setting.settingKey })}
        </h2>

        {/* Meta info */}
        <div
          style={{
            padding: 'var(--spacing-3)',
            background: 'var(--color-surface-muted)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
          }}
        >
          {setting.description && <span>{setting.description}</span>}
          <span>
            {t('systemSettings.card.label.dataType')}: <strong>{setting.dataType}</strong>
            {setting.dataType === 'INTEGER' &&
              setting.minValue !== null &&
              setting.minValue !== undefined &&
              setting.maxValue !== null &&
              setting.maxValue !== undefined &&
              ` (${setting.minValue}~${setting.maxValue})`}
          </span>
          <span>
            {t('systemSettings.card.label.defaultValue')}:{' '}
            <code style={{ fontFamily: 'var(--font-family-mono)' }}>{setting.defaultValue}</code>
          </span>
          <span>
            {t('systemSettings.card.label.currentValue')}:{' '}
            <code style={{ fontFamily: 'var(--font-family-mono)' }}>{setting.value}</code>
          </span>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          noValidate
          aria-label={t('systemSettings.edit.field.value.label')}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            <label
              htmlFor={fieldId}
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
              }}
            >
              {t('systemSettings.edit.field.value.label')}
            </label>

            {/* Widget by dataType */}
            {setting.dataType === 'BOOLEAN' && (
              <BooleanToggleField
                id={fieldId}
                value={currentValue}
                onChange={(v) => setValue('value', v, { shouldValidate: true })}
              />
            )}
            {setting.dataType === 'INTEGER' && (
              <IntegerInputField
                id={fieldId}
                value={currentValue}
                onChange={(v) => setValue('value', v, { shouldValidate: true })}
                min={setting.minValue}
                max={setting.maxValue}
                hasError={!!errors.value}
              />
            )}
            {setting.dataType === 'STRING' && (
              <StringInputField
                id={fieldId}
                value={currentValue}
                onChange={(v) => setValue('value', v, { shouldValidate: true })}
                hasError={!!errors.value}
              />
            )}
            {setting.dataType === 'ENUM' && (
              <EnumSelectField
                id={fieldId}
                value={currentValue}
                onChange={(v) => setValue('value', v, { shouldValidate: true })}
                enumValues={setting.enumValues ?? ''}
                hasError={!!errors.value}
              />
            )}

            {/* Client-side validation error */}
            {errors.value && (
              <p
                id={errorId}
                role="alert"
                aria-live="polite"
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-danger)',
                  margin: 0,
                }}
              >
                {errors.value.message}
              </p>
            )}
          </div>

          {/* Server error (422 / 409) */}
          {errorMessage && (
            <div style={{ marginTop: 'var(--spacing-3)' }}>
              <ErrorState
                title={
                  errorCode === 'SETTING-422-001'
                    ? t('systemSettings.error.SETTING-422-001')
                    : errorCode === 'COMMON-409-002'
                    ? t('systemSettings.error.COMMON-409-002')
                    : t('systemSettings.error.network')
                }
                message={errorMessage}
              />
            </div>
          )}

          {/* Footer buttons */}
          <div
            style={{
              marginTop: 'var(--spacing-6)',
              display: 'flex',
              gap: 'var(--spacing-2)',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('systemSettings.edit.button.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              aria-busy={isLoading}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                opacity: isLoading ? 0.7 : 1,
                minWidth: 80,
              }}
            >
              {isLoading ? t('systemSettings.edit.button.loading') : t('systemSettings.edit.button.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
